import React, { useState, forwardRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar, SnackbarContent } from 'notistack';
import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';
import { Collapse, Link, Stack, Paper, Typography, Card, CardActions, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { clearMovingFiles, moveFiles } from 'store/slices/files';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';
import { Scrollbar } from 'components/Scrollbar';
import { CircularProgress } from '../../common/CircularProgress';

const Root = styled('div')({
  '@media (min-width:600px)': {
    minWidth: '340px !important',
  },
});

const StyledCard = styled(Card)({
  backgroundColor: '#a7c4e5',
  width: '100%',
  borderRadius: '4px',
});

const StyledTypography = styled(Typography)({
  fontWeight: 500,
});

const ActionRoot = styled(CardActions)({
  padding: '4px 16px 4px 16px',
  justifyContent: 'space-between',
});

const Icons = styled('div')({
  marginLeft: 'auto',
});

const ExpandIconButton = styled(IconButton)(({ expanded }) => ({
  padding: '8px 8px',
  transform: expanded === 'true' ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'all .2s',
  color: '#3b70ac',
}));

const CollapsePaper = styled(Paper)({
  padding: '0px',
  borderRadius: '0px 0px 4px 4px',
});

const FileItemContainer = styled(Stack)({
  padding: '8px 16px',
  minHeight: 40,
  alignItems: 'center',
  '&:hover': {
    backgroundColor: '#f9f9f9',
  },
});

const FileNameTypography = styled(Typography)({
  fontSize: '14px',
  fontWeight: '400',
  width: '250px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const IconWrapper = styled('div')({
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const MoveFileItem = ({ file }) => {
  const renderStatusIcon = () => {
    switch (file.status) {
      case 'pending':
        return <CircularProgress size={24} />;

      case 'fulfilled':
        return <CheckCircleOutlineIcon sx={{ fontSize: 24, color: '#63b982' }} />;

      case 'rejected':
        return <HighlightOffIcon sx={{ fontSize: 24, color: '#eb6d48' }} />;

      default:
        return null;
    }
  };

  return (
    <FileItemContainer
      direction="row"
      alignItems="center"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <FileNameTypography
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginRight: 1,
          }}
        >
          {file.fileName}
        </FileNameTypography>

        <IconWrapper>{renderStatusIcon()}</IconWrapper>
      </Stack>
    </FileItemContainer>
  );
};

export const MoveSnackbar = forwardRef(function MoveSnackbar(props, ref) {
  const movingFiles = useSelector((state) => state.files.movingFiles);
  const { id, message } = props;
  const dispatch = useDispatch();
  const dispatchAction = useAsyncDispatch();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const [expanded, setExpanded] = useState(true);

  const handleExpandClick = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleBatchUndo = () => {
    const allFulfilled = movingFiles.filter((item) => item.status === 'fulfilled');

    closeSnackbar(id);
    dispatch(clearMovingFiles());

    if (allFulfilled.length > 0) {
      const oldParent = allFulfilled[0].oldParent;

      const filesWithName = allFulfilled.map((file) => ({
        id: file.id,
        name: file.fileName,
        parent_file: file.newParent,
      }));

      dispatchAction(moveFiles({ files: filesWithName, destination: oldParent, isUndo: true }), {
        onSuccess: () => {
          enqueueSnackbar('Action undone');
        },
      });
    }
  };

  const moveMessage = useCallback(() => {
    const allCompleted = movingFiles.every((item) => item.status !== 'pending');
    const allFulfilled = movingFiles.every((item) => item.status === 'fulfilled');
    const totalFulfilled = movingFiles.filter((item) => item.status === 'fulfilled').length;
    const totalFailed = movingFiles.filter((item) => item.status === 'rejected').length;
    const hasPending = movingFiles.some((item) => item.status === 'pending');
    const maxLen = 20;
    const truncate = (name) => (name.length > maxLen ? name.slice(0, maxLen) + '...' : name);
    const newParentName = movingFiles[0]?.newParent?.name || 'Files';
    const oldParentName = movingFiles[0]?.oldParent?.name || 'Files';

    if (movingFiles.length > 0 && allFulfilled) {
      return `${totalFulfilled} file${totalFulfilled !== 1 ? 's' : ''} moved from ${truncate(oldParentName)} to ${truncate(newParentName)}`;
    }
    if (movingFiles.length > 0 && allCompleted && totalFailed > 0) {
      return `${totalFulfilled} file${totalFulfilled !== 1 ? 's' : ''} moved (${totalFailed} failed)`;
    }
    if (movingFiles.length > 0 && hasPending) {
      const totalProcessed = movingFiles.filter((f) => ['fulfilled', 'rejected'].includes(f.status)).length;
      return `Moving ${totalProcessed} / ${movingFiles.length} files to ${truncate(newParentName)}`;
    }
    return message;
  }, [movingFiles, message]);

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
    dispatch(clearMovingFiles());
  }, [id, closeSnackbar, dispatch]);

  const hasPending = movingFiles.some((item) => item.status === 'pending');

  return (
    <SnackbarContent ref={ref}>
      <Root>
        <StyledCard>
          <ActionRoot>
            <StyledTypography variant="subtitle2">{moveMessage()}</StyledTypography>
            <Icons>
              <ExpandIconButton
                size="small"
                aria-label="Show more"
                expanded={expanded.toString()}
                onClick={handleExpandClick}
              >
                <ExpandMoreIcon />
              </ExpandIconButton>
              {!hasPending && (
                <IconButton
                  size="small"
                  aria-label="Close"
                  onClick={handleDismiss}
                >
                  <CloseIcon sx={{ width: '0.8em', height: '0.8em' }} />
                </IconButton>
              )}
            </Icons>
          </ActionRoot>
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit
          >
            <CollapsePaper>
              {!hasPending && (
                <Stack sx={{ p: 0.5, background: (theme) => theme.palette.grey[200] }}>
                  <Link
                    onClick={() => handleBatchUndo()}
                    variant="body2"
                    underline="none"
                    color="primary"
                    sx={{ cursor: 'pointer', textAlign: 'right', pr: 1.5 }}
                  >
                    Undo
                  </Link>
                </Stack>
              )}
              <Scrollbar
                sx={{
                  maxHeight: '240px',
                  '& .simplebar-content': {
                    height: '100%',
                  },
                  '& .simplebar-scrollbar:before': {
                    background: '#ebebeb',
                  },
                }}
              >
                {movingFiles.map((file) => (
                  <MoveFileItem
                    key={file.id}
                    file={file}
                  />
                ))}
              </Scrollbar>
            </CollapsePaper>
          </Collapse>
        </StyledCard>
      </Root>
    </SnackbarContent>
  );
});

MoveSnackbar.propTypes = {
  sx: PropTypes.object,
};
